// Dependencies
import React, { useRef, useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Divider
} from "@mui/material";
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import RestoreIcon from '@mui/icons-material/Restore';
import LockIcon from '@mui/icons-material/Lock';

// Components
import VidFleetVideo from "Modules/.common/components/VidFleetVideo";
import SmartWitnessSnapshot from 'Modules/.common/components/SmartWitnessSnapshot';
import { MenuComponent } from "Components/Menu";
import { Button, LinearProgress, Tooltip } from "Components";
import { TagsGroup } from "Components/Tag";

// Styles
import "./styles.scss";
import colors from "Assets/sass/core/vars.scss";
import { getVidFleetClipStatus } from "Core/data/VideoEvents";
//actions
import {
  openSharedMediaModal,
  clipRequestOpenModalAddNotes,
  // openVideoAdvancedFilters,
  openDeleteMediaDetailModal,
  removeClipsFromList,
  clearRedux,
  getRefreshClip,
  openModalDeleteVideo
} from "Redux/actions";

import cx from "classnames";
// Constants
import "./../../../events/.components/index.scss";
import { useIntl } from "react-intl";
import { NotificationManager } from "Components/Notifications";
import { useRestoreClipRequests } from "Modules/video/.globals/api/clipRequests";
import { GetFormatForMoment, getTagColor } from "Core/data/Helpers";
import { Timezone } from "Components/uiControls";
import { openModalEventChangeStatus } from "../../../../../videoDetail/views/clip/redux/slice";
import { Loading } from "@gpstrackit/gtc-react-controls";

const labelStyle = (detailsFontSize, detailsLineHeight) => ({
  color: colors.color_disabled_gray,
  fontSize: detailsFontSize,
  lineHeight: detailsLineHeight,
  textAlign: "right",
  whiteSpace: "nowrap",
});

const valueStyle = (detailsFontSize, detailsLineHeight) => ({
  fontSize: detailsFontSize,
  lineHeight: detailsLineHeight,
  fontWeight: "400",
  color: colors.black,
});

// The zone abbreviation (", UTC-5") is a separate flex item: it never splits, and it drops to its own line
// before the range gets squeezed, so a narrow card does not break "UTC-5" over two lines.
const timeFrameStyle = {
  minWidth: 0,
  "& .gtc-timezone": { flexWrap: "wrap" },
  "& .gtc-timezone > div": { whiteSpace: "nowrap" },
};

const LightmetricsVideo = ({
  url,
  details,
}) => {
  const { messages } = useIntl();
  let parsedDetails = JSON.parse(details)
  if(parsedDetails){
    parsedDetails = JSON.parse(parsedDetails)
  }
  const expirationDate = parsedDetails?.expirationDate;
  const isPassed7Days = moment().diff(moment(expirationDate), 'days') > 7;

  return (
    !isPassed7Days ? (
      <video style={{ width: "100%", height: "100%" }} src={url} controls />
      ) : (
        <div style={{ flexDirection: 'column', width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: '#dddddd' }}>
          <Loading show={true}  />
          <Typography style={{ color: 'black' }}>{messages["videoMainView_searchVideo"]}</Typography>
        </div>
      )
  );
};

const ClipCardDetailsGrid = ({
  isMapModal,
  detailsFontSize,
  detailsLineHeight,
  clipDate,
  driver,
  unitName,
  timeFrame,
  requestor,
  requested,
  startDate,
  endDate,
  dateformat,
  unit,
  tags,
  messages,
  getTagColor,
}) => {
  const gridStyles = {
    width: isMapModal ? "100%" : "110%",
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    columnGap: "12px",
    rowGap: "4px",
    alignItems: "start",
    fontSize: "15px",
    justifyContent: isMapModal ? "start" : "center",
  };

  const renderTextValue = (info, truncate = false) => {
    if (truncate && info && info.length > 20) {
      return (
        <Tooltip title={info} placement="bottom">
          <Typography noWrap sx={{ ...valueStyle(detailsFontSize, detailsLineHeight), width: "100%" }}>
            {info.slice(0, 20) + "..."}
          </Typography>
        </Tooltip>
      );
    }
    return (
      <Typography noWrap sx={valueStyle(detailsFontSize, detailsLineHeight)}>
        {info ?? messages["videoClipRequest_notAvailable"]}
      </Typography>
    );
  };

  return (
    <Grid style={{ display: "flex", justifyContent: "start" }}>
      <Box sx={gridStyles}>
        {/* Clip date */}
        <Typography sx={labelStyle(detailsFontSize, detailsLineHeight)}>{clipDate?.title} :</Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={valueStyle(detailsFontSize, detailsLineHeight)}>{clipDate?.info}</Typography>
        </Box>

        {/* Driver */}
        <Typography sx={labelStyle(detailsFontSize, detailsLineHeight)}>{driver?.title} :</Typography>
        <Box sx={{ minWidth: 0 }}>{renderTextValue(driver?.info, true)}</Box>

        {/* Unit name */}
        <Typography sx={labelStyle(detailsFontSize, detailsLineHeight)}>{unitName?.title} :</Typography>
        <Box sx={{ minWidth: 0 }}>{renderTextValue(unitName?.info, true)}</Box>

        {/* Time frame */}
        <Typography sx={labelStyle(detailsFontSize, detailsLineHeight)}>{timeFrame?.title} :</Typography>
        <Box sx={timeFrameStyle}>
          <Timezone
            range={{ start: startDate, end: endDate }}
            dateFormat={dateformat}
            timeFormat="hh:mm a"
            timezone={unit?.timeZone?.name}
            onlyHour
          />
        </Box>

        {/* Requestor */}
        <Typography sx={labelStyle(detailsFontSize, detailsLineHeight)}>{requestor?.title} :</Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={valueStyle(detailsFontSize, detailsLineHeight)}>{requestor?.info}</Typography>
        </Box>

        {/* Requested */}
        <Typography sx={labelStyle(detailsFontSize, detailsLineHeight)}>{requested?.title} :</Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={valueStyle(detailsFontSize, detailsLineHeight)}>{requested?.info}</Typography>
        </Box>

        {/* Tags */}
        <Typography sx={labelStyle(detailsFontSize, detailsLineHeight)}>{messages["videoMainView_tags"]} :</Typography>
        <Box  sx={{ minWidth: 0, marginTop: tags?.length === 1 ? "0px" : "10px" }}>
          <TagsGroup
            options={tags?.length ? tags?.map((t) => ({ ...t, color: getTagColor(t?.color) })) : []}
            nameField="label"
            colorField="color"
          />
        </Box>
      </Box>
    </Grid>
  );
};

const ClipCard = (props) => {
  const { row, hiddenDeleteButton, loading, unit, isMapModal: isMapModalProp, getLabel } = props;
  const { messages, locale } = useIntl();
  const { mutate: restoreClip } = useRestoreClipRequests({
    onError: () => NotificationManager.error(messages["errorMessage"], messages["error"], 3000, null, null, "warning"),
  });
  const [clipDate, setClipDate] = useState();
  const [unitName, setUnitName] = useState();
  const [driver, setDriver] = useState()
  const [timeFrame, setTimeFrame] = useState();
  const [requestor, setRequestor] = useState();
  const [requested, setRequested] = useState();
  const [notesLabel, setNotesLabel] = useState();

  // Hooks
  const dispatch = useDispatch();
  const vidFleetVideoRef = useRef();
  const {
    status,
    startDate,
    endDate,
    createdOn,
    deviceModelName,
    deviceId,
    id,
    clipID,
    notes,
    starred,
    thumbnailUrl,
    createdBy,
    createdByUser,
    driverName,
    driverExternalId,
    label,
    transactionId,
    details,
    url,
    unit: rowUnit,
    isMapModal: isMapModalRow,
    isPrivate,
  } = row;

  const rowUnitData = rowUnit ?? {};
  const tags = rowUnitData?.tags ?? row?.tags ?? [];

  const isMapModal = Boolean(isMapModalProp ?? isMapModalRow);
  const headerFontSize = isMapModal ? "21px" : "18px";
  const headerMarginLeft = isMapModal ? "50px" : "16px";
  const detailsFontSize = isMapModal ? "15px" : "14px";
  const detailsLineHeight = isMapModal ? "22px" : "19px";
  const headerIconOffsetTop = isMapModal ? "-22px" : "0px";

  const { loadingUpdateClipRequest, updateClipRequestResponse } = useSelector(
    (state) => state.videoDetailRedux
  );

  const { user } = useSelector((state) => state.securityRedux);
  const refreshClipRequest = useSelector((state) => state.catalogsRedux.refreshClipRequest);

  const isCompleteRequested =
    status === "finished" || status === "mp4RemuxFinished" || status === "mp4Uploaded" ? true : false;
  const { dateformat, timeZone } = user;
  const format = GetFormatForMoment(dateformat);

  useEffect(() => {
    if (updateClipRequestResponse[id]) {
      dispatch(
        clearRedux("VIDEO_DETAIL", {
          updateClipRequestResponse: {
            ...updateClipRequestResponse,
            [id]: null,
          },
          loadingUpdateClipRequest: {
            ...loadingUpdateClipRequest,
            [id]: false,
          },
        })
      );
    }
  }, [updateClipRequestResponse[id]]);

  // useEffect(() => {
  //   if (refreshClipRequest?.transactionId === transactionId) {
  //     dispatch(getRefreshClip({
  //       id
  //     }));
  //     dispatch(clearRedux("CATALOG_REDUX", { refreshClipRequest: null }));
  //   }
  // }, [refreshClipRequest?.status])


  useEffect(() => {
    getClipDate(startDate);
    getDriverUnit({ driverName, label });
    getTimeFrame(startDate, endDate);
    getRequested(createdOn);
    getRequestor(createdByUser);
    getNotes(notes);
  }, [row, locale]);

  const getClipDate = (date) => {
    const formatDate = moment.unix(date / 1000).format(format);
    setClipDate({
      title: messages["videoClipRequest_clipDate"],
      info: formatDate,
    });
  };

  const getDriverUnit = ({ driverName, label }) => {
    let driver = "";
    let unit = "";

    if (driverName) {
      driver = `${driverName}${driverExternalId ? ` (${driverExternalId})` : ""}`;
    } else {
      driver = messages["videoClipRequest_unassigned"];
    }

    setDriver({
      title: messages["videoClipRequest_driver"],
      info: driver,
    })

    if (label) {
      unit = `${label}`;
    } else {
      unit = messages["videoClipRequest_unassigned"];
    }

    setUnitName({
      title: messages["videoClipRequest_unit"],
      info: unit,
    })
  };

  const getTimeFrame = (start, end) => {
    const formatDateStart = moment.unix(start / 1000).format("h:mm:ss  A");
    const formatDateEnd = moment.unix(end / 1000).format("h:mm:ss A");
    const finalFormat = `${formatDateStart} - ${formatDateEnd}`;
    setTimeFrame({
      title: messages["videoClipRequest_timeFrame"],
      info: finalFormat,
    });
  };

  const getRequestor = ({ username }) => {
    let label = messages["videoClipRequest_notAvailable"];
    if (username) {
      label = messages[username] || username;
    }
    setRequestor({
      title: messages["videoClipRequest_requestor"],
      info: label,
    });
  };

  const getNotes = (notes) => {
    let label = messages["videoClipRequest_notAvailable"];
    if (notes) {
      label = notes;
    }
    setNotesLabel({ title: messages["videoClipRequest_notes"], info: label });
  };

  const getRequested = (date) => {
    const formatDate = moment(new Date(date * 1000)).format(format);
    setRequested({
      title: messages["videoClipRequest_requested"],
      info: formatDate,
    });
  };

  const clipStatus = getVidFleetClipStatus(status);

  const contextualCardMenuConfig = [];

  if (status === "mp4RemuxFinished" && !hiddenDeleteButton)
    contextualCardMenuConfig.push(messages["videoMainView_share"]);
  if (!hiddenDeleteButton)
    contextualCardMenuConfig.push(messages["videoMainView_delete"]);


  const options = {
    messages: messages, // For to access to language keys
    mode: status === "mp4RemuxFinished" || status === "mp4Uploaded" ? "video" : "picture",
    type: "playback", //Depends of the playing type, the url VidFleet API changes
    id: clipID, // This could be a ESN or Clip Id
    autoplay: false, // If is true the video will be played automatically
    deviceModel: deviceModelName, //4k|360
    thumbnailUrl:
      status !== "mp4RemuxFinished" || status !== "mp4Uploaded" ? clipStatus?.image : thumbnailUrl,
    disabled: status === "mp4RemuxFinished" || status === "mp4Uploaded" ? false : true,
  };

  const formatedDate = moment.unix(startDate / 1000).format("llll");
  let color = status === "mp4RemuxFinished" || status === "mp4Uploaded" ? "black" : "white";

  const chooseIcon = () => {
    switch (status) {
      case "requestAccepted":
      case "startUpload":
      case "uploading":
      case "finished":
        return (
          <div
            style={{
              minHeight: "240px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: colors.color_request_accepted,
            }}
          >
            <RotateRightIcon
              style={{
                fontSize: "60px",
                opacity: "0.4",
                color: colors.color_request_white,
              }}
            />
          </div>
        );
      case "timeout":
      case "interrupted":
      case "requestDecline":
      case "unknown":
        return (
          <div
            style={{
              height: "240px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: colors.color_request_requestDecline,
            }}
          >
            <VideocamOffIcon
              style={{
                fontSize: "60px",
                opacity: "0.4",
                color: colors.color_request_white,
              }}
            />
          </div>
        );
      case "requestPending":
        return (
          <div
            style={{
              height: "240px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: colors.color_request_requestPending,
            }}
          >
            <RestoreIcon
              style={{
                fontSize: "60px",
                opacity: "0.4",
                color: colors.color_request_white,
              }}
            />
          </div>
        );
    }
  };
  
  return (
    <Card style={{ borderRadius: "20px" }}>
      <Grid
        className="clip-card-media-wrap"
        style={{
          display: "flex",
          justifyContent: "center",
          backgroundColor: color,
          position: "relative",
        }}
      >
        {isPrivate === true && (
          <Tooltip title={messages["videoClipRequest_privateOnlyVisible"]} placement="top">
            <Box
              component="span"
              className="clip-card-private-lock"
              aria-label={messages["videoClipRequest_privateOnlyVisible"]}
            >
              <LockIcon fontSize="medium" />
            </Box>
          </Tooltip>
        )}
        <Grid className={cx("vidFleet-video-container", "ctn-vid-fleet-video")}>

          {
            status == "mp4RemuxFinished" && deviceModelName !== "SmartWitness" &&
            <VidFleetVideo ref={vidFleetVideoRef} {...options} />
          }

          {
            status == "mp4RemuxFinished" && deviceModelName == "SmartWitness" &&
            <SmartWitnessSnapshot ref={vidFleetVideoRef} height={240} indicators={false} vehicleTrail={{ deviceData: details }} />
          }

          {
            status == "mp4Uploaded" && deviceModelName == "Mitac-Gemini" &&
            <LightmetricsVideo url={url} details={details} />
          }

          {
            status == "mp4Uploaded" && deviceModelName == "Mitac" &&
            <LightmetricsVideo url={url} details={details} />
          }

          {
            status !== "mp4RemuxFinished" && status !== "mp4Uploaded" &&
            chooseIcon()
          }
        </Grid>
      </Grid>
      <CardContent style={{ minHeight: "210px" }}>
        <Grid
          justifyContent="space-between"
          alignItems="flex-start"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <Box
            wrap="nowrap"
            spacing={2}
            style={{
              display: "flex",
              justifyContent: isMapModal ? "flex-start" : "center",
              alignItems: "center",
              width: isMapModal ? "100%" : undefined,
            }}

          >
            <Grid>
              <img
                src={clipStatus?.icon}
                alt={clipStatus?.lanKey}
                style={{ display: "block", marginTop: headerIconOffsetTop }}
              />
            </Grid>
            <div
              onClick={() => {
                !isMapModal && dispatch(openModalEventChangeStatus({ open: true, extraData: row }))
              }}
              style={{
                color: colors.black,
                fontWeight: "500",
                fontSize: headerFontSize,
                marginLeft: headerMarginLeft,
              }}
            >
              {!isMapModal && isCompleteRequested ? (
                <Button
                  type="link"
                  url={`/app/video/videoDetail/clip/${id}?deviceId=${deviceId}`}
                >
                  {messages[clipStatus?.lanKey]}
                </Button>
              ) : (
                !isMapModal ? (
                  <div>{messages[clipStatus?.lanKey]}</div>
                ) : (
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {messages[clipStatus?.lanKey]}
                  </div>
                )
              )}
            </div>
          </Box>
          <Divider style={{ margin: "10px 0", width: "100%" }} />
          <ClipCardDetailsGrid
            isMapModal={isMapModal}
            detailsFontSize={detailsFontSize}
            detailsLineHeight={detailsLineHeight}
            clipDate={clipDate}
            driver={driver}
            unitName={unitName}
            timeFrame={timeFrame}
            requestor={requestor}
            requested={requested}
            startDate={startDate}
            endDate={endDate}
            dateformat={dateformat}
            unit={rowUnitData}
            tags={tags}
            messages={messages}
            getTagColor={getTagColor}
          />
            <Box
              style={{
                display: "flex",
                justifyContent: "flex-end",
                position: "absolute",
                right: "8px",
                bottom: "5px",
                width: "auto",
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              <Box style={{ display: "flex", gap: "4px", pointerEvents: "auto" }}>
                {(!isMapModal && !hiddenDeleteButton && status === "mp4RemuxFinished" && deviceModelName != "360") && (
                  <Button
                    type="icon"
                    icon="download"
                    tooltip={deviceModelName === "360" ? messages['videoMainView_download360Error'] : messages["download"]}
                    disabled={
                      loadingUpdateClipRequest[id] ||
                      (status !== "finished" &&
                        status !== "mp4RemuxFinished") ||
                      deviceModelName === "360"
                    }
                    onClick={(e) => {
                      if (e) e.preventDefault();
                      if (vidFleetVideoRef?.current)
                        vidFleetVideoRef?.current.downloadVideo();
                    }}
                  />
                )}
                { 
                !isMapModal &&
                <Grid>
                  <MenuComponent
                    disabled={loading || loadingUpdateClipRequest[id]}
                    options={contextualCardMenuConfig}
                    icon={"more_vert"}
                    className="icon-menu-vehicle-service showInHover"
                    onClick={(type) => {
                      switch (type) {
                        case messages["videoMainView_addNote"]:
                          // dispatch(openVideoAdvancedFilters(false));
                          dispatch(
                            clipRequestOpenModalAddNotes(true, {
                              id: row?.id,
                              label: getLabel(row),
                            })
                          );
                          break;
                        case messages["videoMainView_share"]:
                          if (
                            status === "finished" ||
                            status === "mp4RemuxFinished"
                          ) {
                            // dispatch(openVideoAdvancedFilters(false));
                            dispatch(
                              openSharedMediaModal({
                                open: true,
                                items: [
                                  {
                                    entity: {
                                      id: row?.id,
                                      name: "ClipRequest",
                                      label: getLabel(row),
                                    },
                                    deviceId: deviceId,
                                  },
                                ],
                              })
                            );
                          }
                          break;
                        case messages["videoMainView_delete"]:
                          // dispatch(openVideoAdvancedFilters(false));
                          dispatch(openModalDeleteVideo({
                            type: 'clip',
                            items: [{ id: id, label: getLabel(row) }],
                          }))
                          break;
                        case messages["videoMainView_restore"]:
                          restoreClip([id]);
                          break;

                        default:
                          break;
                      }
                    }}
                    value={row}
                    visible
                  />
                </Grid>
                }
              </Box>
            </Box>
          </Grid>
      </CardContent>
      {loadingUpdateClipRequest[id] && refreshClipRequest?.transactionId === transactionId && <LinearProgress color="primary" />}
    </Card>
  );
};

export default ClipCard;
